import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useMutation, useQueryClient } from 'react-query';
import { createGroup, updateGroup } from '../../services/iam';
import { Group } from '../../types/iam';
import { toast } from '@/lib/toast';
import { Button, Input, Modal, Textarea } from '@/components/ds-zta';
import { apiErrorMessage, descriptionField, keyFromName, KEY_PATTERN, KEY_PATTERN_MESSAGE } from './iamForm';

interface Props {
  /** The group to edit; omit to create one. */
  group?: Group;
  onClose: () => void;
}

/* Create and edit one group. Create asks for a key (suggested from the name
   until the user types one); edit changes name and description only, as the
   group's key is its identity. */
const GroupFormModal: React.FC<Props> = ({ group, onClose }) => {
  const isEdit = !!group;
  const queryClient = useQueryClient();
  const { register, handleSubmit, setValue, formState: { errors } } = useForm<Partial<Group>>({
    defaultValues: group ? { name: group.name, description: group.description } : undefined,
  });
  const [keyTouched, setKeyTouched] = useState(false);

  const mutation = useMutation(
    (data: Partial<Group>) => (group ? updateGroup(group.id, data) : createGroup(data)),
    {
      onSuccess: () => {
        if (group) queryClient.invalidateQueries(['group', group.id]);
        queryClient.invalidateQueries('groups');
        toast.success(isEdit ? 'Group updated successfully' : 'Group created successfully');
        onClose();
      },
      onError: (err: unknown) => {
        toast.error(`Failed to ${isEdit ? 'update' : 'create'} group: ${apiErrorMessage(err)}`);
      },
    },
  );

  const onSubmit = (data: Partial<Group>) => {
    const payload: Partial<Group> = isEdit
      ? { name: data.name?.trim() }
      : { name: data.name?.trim(), key: data.key?.trim() };
    mutation.mutate({ ...payload, ...descriptionField(data.description, group?.description) });
  };

  const nameField = register('name', { required: 'Name is required' });
  const formId = isEdit ? 'edit-group-form' : 'create-group-form';

  return (
    <Modal
      open
      onClose={onClose}
      title={isEdit ? 'Edit Group' : 'Create New Group'}
      size="sm"
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>Cancel</Button>
          <Button type="submit" form={formId} loading={mutation.isLoading}>
            {isEdit ? 'Save Changes' : 'Create Group'}
          </Button>
        </>
      }
    >
      <form id={formId} onSubmit={handleSubmit(onSubmit)} className="space-y-4">
        <Input
          {...nameField}
          onChange={(e) => {
            nameField.onChange(e);
            if (!isEdit && !keyTouched) {
              setValue('key', keyFromName(e.target.value), { shouldValidate: !!errors.key });
            }
          }}
          id="name"
          label="Name"
          error={errors.name?.message}
        />
        {!isEdit && (() => {
          const keyField = register('key', {
            required: 'Key is required',
            pattern: { value: KEY_PATTERN, message: KEY_PATTERN_MESSAGE },
          });
          return (
            <Input
              {...keyField}
              onChange={(e) => {
                setKeyTouched(e.target.value !== '');
                keyField.onChange(e);
              }}
              id="key"
              label="Key"
              help="Suggested from the name. Lowercase letters, numbers, - and _."
              error={errors.key?.message}
            />
          );
        })()}
        <Textarea {...register('description')} id="description" label="Description" rows={3} />
      </form>
    </Modal>
  );
};

export default GroupFormModal;
